<template>
  <div class="navbar">
    <hamburger :is-active="sidebar.opened" class="hamburger-container" @toggleClick="toggleSideBar" />

    <breadcrumb class="breadcrumb-container" />

    <div class="right-menu">
      <!-- 用户信息 -->
      <el-dropdown class="avatar-container" trigger="click">
        <div class="avatar-wrapper">
          <div class="user-info">
            <span class="user-name">{{ name || '管理员' }}</span>
            <span class="user-role">系统管理员</span>
          </div>
          <div class="avatar-circle">
            <img v-if="avatar" :src="avatar" class="user-avatar">
            <i v-else class="el-icon-user-solid default-avatar"></i>
          </div>
          <i class="el-icon-caret-bottom" />
        </div>
        <el-dropdown-menu slot="dropdown" class="user-dropdown">
          <el-dropdown-item @click.native="openChangePassword">
            <i class="el-icon-key"></i>
            <span>修改密码</span>
          </el-dropdown-item>
          <el-dropdown-item divided @click.native="logout">
            <i class="el-icon-switch-button"></i>
            <span>退出登录</span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 修改密码弹窗 -->
    <el-dialog title="修改密码" :visible.sync="pwdDialogVisible" width="420px" :close-on-click-modal="false" append-to-body>
      <el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules" label-width="100px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="pwdForm.oldPassword" type="password" show-password placeholder="请输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" show-password placeholder="至少 6 位" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" show-password placeholder="再次输入新密码" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="pwdDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="pwdLoading" @click="submitChangePassword">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Breadcrumb'
import Hamburger from '@/components/Hamburger'
import { changePassword } from '@/api/user'

export default {
  components: {
    Breadcrumb,
    Hamburger
  },
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.pwdForm.newPassword) {
        callback(new Error('两次输入的新密码不一致'))
      } else {
        callback()
      }
    }
    return {
      pwdDialogVisible: false,
      pwdLoading: false,
      pwdForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      pwdRules: {
        oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '新密码至少 6 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters([
      'sidebar',
      'avatar',
      'name'
    ])
  },
  methods: {
    toggleSideBar() {
      this.$store.dispatch('app/toggleSideBar')
    },
    openChangePassword() {
      this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.pwdDialogVisible = true
      this.$nextTick(() => {
        this.$refs.pwdForm && this.$refs.pwdForm.clearValidate()
      })
    },
    submitChangePassword() {
      this.$refs.pwdForm.validate(async valid => {
        if (!valid) return
        this.pwdLoading = true
        try {
          await changePassword({
            oldPassword: this.pwdForm.oldPassword,
            newPassword: this.pwdForm.newPassword
          })
          this.pwdDialogVisible = false
          this.$message.success('密码修改成功，请用新密码重新登录')
          // 纯客户端清除 token 并强制重登（不依赖后端 logout 接口）
          await this.$store.dispatch('user/resetToken')
          this.$router.push('/login')
        } catch (e) {
          // 错误提示已由 request 拦截器统一弹出，这里不再重复提示
        } finally {
          this.pwdLoading = false
        }
      })
    },
    async logout() {
      await this.$store.dispatch('user/logout')
      this.$router.push(`/login?redirect=${this.$route.fullPath}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.navbar {
  height: 60px;
  overflow: hidden;
  position: relative;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-bottom: 1px solid #f0f2f5;

  .hamburger-container {
    line-height: 60px;
    height: 100%;
    float: left;
    cursor: pointer;
    transition: all .3s ease;
    padding: 0 15px;
    -webkit-tap-highlight-color:transparent;

    &:hover {
      background: rgba(64, 158, 255, 0.1);
      color: #409EFF;
    }
  }

  .breadcrumb-container {
    float: left;
    line-height: 60px;
    margin-left: 10px;
  }

  .right-menu {
    float: right;
    height: 100%;
    display: flex;
    align-items: center;
    padding-right: 20px;

    &:focus {
      outline: none;
    }


    .avatar-container {
      .avatar-wrapper {
        display: flex;
        align-items: center;
        cursor: pointer;
        padding: 8px 12px;
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(64, 158, 255, 0.1);
        }

        .user-info {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          margin-right: 10px;
          
          .user-name {
            font-size: 14px;
            font-weight: 600;
            color: #303133;
            line-height: 1;
          }
          
          .user-role {
            font-size: 12px;
            color: #909399;
            margin-top: 2px;
          }
        }

        .avatar-circle {
          position: relative;
          
          .user-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            border: 2px solid #f0f2f5;
          }
          
          .default-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: linear-gradient(135deg, #409EFF, #66b1ff);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
          }
        }

        .el-icon-caret-bottom {
          margin-left: 8px;
          font-size: 12px;
          color: #909399;
          transition: transform 0.3s ease;
        }
        
        &:hover .el-icon-caret-bottom {
          transform: rotate(180deg);
        }
      }
    }
  }
}
</style>

<!-- 用户下拉菜单：el-dropdown-menu 默认挂到 <body>，scoped 样式命中不到，故用非 scoped 全局块。
     统一图标固定宽度 + flex 对齐，保证「修改密码 / 退出登录」两项文字左边缘对齐 -->
<style lang="scss">
.user-dropdown {
  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    padding: 12px 16px;

    i {
      width: 16px;
      margin-right: 8px;
      text-align: center;
      color: #409EFF;
    }

    span {
      line-height: 1;
    }

    &:hover {
      background: #f5f7fa;
    }
  }
}
</style>
